/**
 * #2080 — plain-language copy for the deploy ("Save to TV") failure
 * banner.
 *
 * The core / CLI throw technical `CrucibleError`s (`CRUCIBLE-2xx` /
 * `CRUCIBLE-5xx`) whose `.message` names branches, GitHub, and CI —
 * correct for the CLI + logs, wrong for a GUI-only user who has never
 * seen a terminal. This module translates a known deploy-failure code
 * into a plain headline (and an optional next step) a non-technical
 * user understands, in the product's "Save to TV" vocabulary. The
 * translation lives in the RENDERER on purpose: the plain-language
 * rule is GUI-scoped, so the core/CLI error text stays technical and
 * only the banner is plain.
 *
 * The raw technical message is NEVER discarded — the banner keeps it
 * in a "Technical details" expander (see Workspace.tsx). So this is a
 * headline translation, not a replacement: unknown codes fall back to
 * a generic headline and the expander still carries the real error.
 */

export interface DeployFailureCopy {
  /**
   * Plain-language headline — no jargon (no "branch", "GitHub", "CI",
   * "push", "deploy"). The test: would someone's parent who has never
   * worked in software understand it?
   */
  headline: string;
  /** Optional one-line actionable next step, when one exists. */
  nextStep?: string;
}

const GENERIC_HEADLINE = "We couldn't send your game to the TV.";

/**
 * Known deploy-failure codes → plain copy. Extend this table (not the
 * banner) when a new deploy `CrucibleError` code needs a friendly
 * headline. A code absent here falls back to {@link GENERIC_HEADLINE}
 * — safe because the raw message is still shown in the expander.
 *
 * Codes are the `CrucibleError.code` values thrown along the deploy
 * path in `packages/crucible/src/core/publish.ts` (and the GUI
 * clone-block in `publish-run.ts`).
 */
const COPY_BY_CODE: Record<string, DeployFailureCopy> = {
  // Pre-push branch guard (#2080): committed work is on a branch other
  // than the deploy branch, so nothing new reached the TV. Descriptive
  // code (not CRUCIBLE-206, which github.ts already owns).
  'CRUCIBLE-DEPLOY-WRONG-BRANCH': {
    headline:
      "Your latest changes aren't saved to the main version of your game yet, so there was nothing new to send to the TV.",
    nextStep: 'Save your changes to the main version, then try sending to the TV again.',
  },
  // CRUCIBLE-501 is overloaded across the deploy path (no CI run
  // created, GitHub Actions query failed, CI concluded failure), so we
  // deliberately do NOT claim "already up to date" here — that would
  // mislabel a genuine failure. The "already up to date / nothing to
  // send" case is detected UPFRONT by the readiness gate (which
  // disables the button before a deploy is even attempted), not
  // inferred from this ambiguous terminal code. Keep it a plain,
  // safe, retryable headline; the raw reason is in the expander.
  'CRUCIBLE-501': {
    headline: "We couldn't confirm your game finished sending to the TV.",
    nextStep: 'Please try sending it again in a moment.',
  },
  // git push failed (auth / network / rejected).
  'CRUCIBLE-205': {
    headline: 'Something went wrong while sending your game to the TV.',
    nextStep: 'Check your internet connection and that you are signed in, then try again.',
  },
  // Local quality-gate failed before anything was sent (lint /
  // typecheck / build errors in the game). The list of which checks
  // failed lives in the collapsed "Technical details" below, so the
  // next step points there rather than claiming the list is already
  // visible (Bugbot #3493 — "listed below" over-promised).
  'CRUCIBLE-210': {
    headline: 'Your game has a few problems that need fixing before it can go to the TV.',
    nextStep:
      "Open 'Technical details' below to see what needs fixing, then try sending to the TV again.",
  },
  // Prebuilt-Ingot bundle missing from the publish tree. The recovery
  // is deep tooling/gitignore work a GUI-only user can't do, so give
  // an honest headline (no false "you can fix this yourself") — the
  // real reason stays in the expander for whoever can act on it.
  'CRUCIBLE-211': {
    headline: "Something in your game's setup needs attention before it can go to the TV.",
    nextStep: 'Try sending it again, and if it keeps happening, let the team know.',
  },
  // Uncommitted changes blocked the deploy (rare now that deploy
  // auto-saves outstanding work, but still possible).
  'CRUCIBLE-202': {
    headline: 'Please save your latest changes first, then send to the TV again.',
  },
  // Game isn't linked to a place to save to.
  'CRUCIBLE-203': {
    headline: "This game isn't set up to send to a TV yet.",
  },
  // Community-clone deploy block (thrown in publish-run.ts).
  'CRUCIBLE-FOUNDRY-CLONE-PUBLISH-BLOCKED': {
    headline: "This is a shared community game, so it can't be sent to the TV from here.",
    nextStep: 'Make your own copy of the game first, then send that to the TV.',
  },
};

/**
 * Translate a deploy-failure `CrucibleError` code into plain-language
 * banner copy. `code` is what {@link unflattenIpcError}`(err).code`
 * yields on the renderer; `undefined` (a non-Crucible error) falls
 * back to the generic headline.
 */
export function deployFailureCopy(code: string | undefined): DeployFailureCopy {
  if (code && code in COPY_BY_CODE) {
    return COPY_BY_CODE[code]!;
  }
  return { headline: GENERIC_HEADLINE };
}
